import {
  AfterViewInit,
  Component, HostBinding, inject,
  Injector, OnInit,
  Optional
} from '@angular/core';
import {CommonModule} from "@angular/common";
import { DISPLAY_TYPE, IWidgetConfig, StylableComponent, provideAsWidgetRef, Parent, register, styler, propFactory as PropertiesFactory } from "@wm/components-base";
import {
  concat, fill, first,
  forEach, forEachRight,
  includes, indexOf,
  intersection,
  isArray,
  isObject, join,
  keys, max,
  maxBy,
  minBy, orderBy,
  replace, set,
  split,
  uniq, groupBy, isUndefined,
  get, map, isFunction, debounce
} from "lodash-es";
import {
  allShapes, chartTypes,
  getClonedObject,
  getDateList,
  getSampleData,
  initChart, isAreaChart,
  isAxisDomainValid,
  isBubbleChart, isChartDataArray, isChartDataJSON, isLineTypeChart,
  isPieType, postPlotChartProcess
} from "./chart.utils";
import { Subscription } from "rxjs";
import { ScriptLoaderService, isNumberType, triggerFn, prettifyLabels, fetchPropertiesMapColumns, isEmptyObject, removeAttr, runtimeEventManager, getCanvasElement } from "@wm/core";
import { hideOrShowProperties } from "../../formWidgetUtils";

declare const d3: any,nv: any;
const DEFAULT_CLS = 'app-chart';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-chart',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

const options: {[key: string]:string[]} = {
  'Bubble' : ['bubblesize', 'shape']
},
  NONE = 'none',
  advanceDataProps = ['aggregation', 'aggregationcolumn', 'groupby', 'orderby','shape'],
  // XPaths to get actual data of data points in charts
  chartDataPointXpath = {
    'Column'         : 'rect.nv-bar',
    'Bar'            : 'g.nv-bar',
    'Area'           : '.nv-stackedarea .nv-point',
    'Cumulative Line': '.nv-cumulativeLine .nv-scatterWrap path.nv-point',
    'Line'           : '.nv-lineChart .nv-scatterWrap path.nv-point',
    'Pie'            : '.nv-pieChart .nv-slice path',
    'Donut'          : '.nv-pieChart .nv-slice path',
    'Bubble'         : '.nv-scatterChart .nv-point-paths path'
  },
  // all properties of the chart
  allOptions = ['bubblesize', 'shape'],
  styleProps = {
    'fontunit'      : 'font-size',
    'fontsize'      : 'font-size',
    'color'         : 'fill',
    'fontfamily'    : 'font-family',
    'fontweight'    : 'font-weight',
    'fontstyle'     : 'font-style',
    'textdecoration': 'text-decoration'
  },
  // Getting the relevant aggregation function based on the selected option
  aggregationFnMap: {[key: string]: string} = {
    'average' : 'AVG',
    'count'   : 'COUNT',
    'maximum' : 'MAX',
    'minimum' : 'MIN',
    'sum'     : 'SUM'
  };

const getBooleanValue = (val: any) => {
  if (val === true || val === 'true') {
    return true;
  }
  if (val === false || val === 'false') {
    return false;
  }
  return val;
};

// returns orderby columns and their orders in two separate arrays
const getLodashOrderByFormat = (orderby: any) => {
  let columns;
  const orderByColumns: string[] = [],
    orders: any[] = [];
  forEach(split(orderby, ','), function (col) {
    columns = split(col, ':');
    orderByColumns.push(columns[0]);
    orders.push(columns[1]);
  });
  return {
    'columns' : orderByColumns,
    'orders'  : orders
  };
};

// Replacing the '.' by the '$' because '.' is not supported in the alias names
const getValidAliasName = (aliasName: string) => aliasName ? aliasName.replace(/\./g, '$') : null;

// Applying the font related styles for the chart
const setTextStyle = (properties: any, id: string) => {
  const charttext = d3.select('#wmChart' + id + ' svg').selectAll('text');
  charttext.style(properties);
};

const angle = (d: any) => {
  const a = (d.startAngle + d.endAngle) * 90 / Math.PI - 90;
  return a > 90 ? a - 180 : a;
};
  @Component({
  selector: '[wmChart]',
  template: `
    <div class="panel-heading" *ngIf="title">
      <h3 class="panel-title">
        <div class="pull-left"><i class="app-icon panel-icon {{iconclass}}" [class.ng-hide]="!iconclass"></i></div>
        <div class="heading" [innerHTML]="title"></div>
        <div class="description" *ngIf="subheading" [innerHTML]="subheading"></div>
      </h3>
    </div>
    <div class="panel-heading" *ngIf="isLoadInProgress || bundleLoading">
      <i class="fa fa-spinner fa-spin"></i>
      <span> Loading...</span>
    </div>
    <div class="app-chart-inner" [ngClass]="{'loading':isLoadInProgress || bundleLoading, 'panel-body': title}">
      <svg></svg>
      <div class="wm-content-info readonly-wrapper" *ngIf="showContentLoadError && showNoDataMsg">
        <p class="wm-message" [title]="hintMsg" [ngClass]="{'error': invalidConfig}" [innerText]="errMsg"></p>
      </div>
    </div>
  `,
    styleUrls: ['../../../../../../node_modules/@wavemaker.com/nvd3/build/nv.d3.min.css'],
  providers: [
    provideAsWidgetRef(ChartComponent)
  ],
  exportAs: 'wmChart',
  standalone: true,
  imports: [ CommonModule ],
  styles: [],
})
export class ChartComponent extends StylableComponent implements AfterViewInit,OnInit{
  static initializeProps = register('wm-chart');

  public subheading: string;
  public iconclass: string;
  public isLoadInProgress: boolean;
  public bundleLoading: boolean;
  public showContentLoadError: boolean;
  public showNoDataMsg: boolean = true;
  public hintMsg: string;
  public errMsg: string;
  public invalidConfig: boolean;
  public chartData: any[] = [];
  public isVisuallyGrouped: boolean = false;
  public _processedData: any;
  public type: string;
  private sampleData: any;
  private xDataKeyArr: string[];
  private binddataset: any;
  private xaxisdatakey: any;
  private yaxisdatakey: any;
  private scopedataset: any;
  private $id: any;
  private chart: any;
  private clearCanvas: boolean;
  private chartReady: boolean;
  private showlabels: any;
  private showlabelsoutside: any;
  private theme: string;
  private chartEventListener: Subscription = new Subscription();
  private groupby: any;
  private axisoptions: any;
  private numericColumns: string[] = [];
  private aggregationcolumn: any;
  private datasource: any;
  private nonPrimaryColumns: string[] =[];
  private aggregation: any;
  private filterFields: any;
  private orderby: any;
  private dataset: any;
  private xAxisDataType: string;
  private bubblesize: any;
  private shape: string;
  private isGroupByChecked: boolean;
  private widgetDataset: any;
  private isLiveVariable: boolean;
  private isServiceVariable: any;
  variableService: any;
  propertiesMap: any;
  variableObj: any;
  private offsettop: any;
  private offsetright: any;
  private offsetbottom: any;
  private offsetleft: any;
  private debouncedFn: Function;
  @HostBinding('class.panel') title: string;
  private scriptLoaderService: ScriptLoaderService;

  isGroupByEnabled(activeWidget: any) {
    return activeWidget.groupby && activeWidget.groupby.length !== 0 && activeWidget.groupby !==NONE
    // return !!(this.groupby && this.groupby !== NONE);
  }

  isValidAxis() {
    // Check if x axis and y axis are chosen and are not equal
    return this.binddataset ? (this.xaxisdatakey && this.yaxisdatakey) : true;
  }

  private isAggregationEnabled(activeWidget: any) {
    return !!((activeWidget.isGroupByEnabled(activeWidget) && activeWidget.aggregation !== NONE && activeWidget.aggregationcolumn));
  }

    constructor(inj: Injector, @Optional() viewParent: Parent) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this.scriptLoaderService = inject(ScriptLoaderService);
    // generate unique id for the component
    this.$id = this.widgetId || this.getRandomInt(1, 1000);
    // remove title attribute as the element on hover shows you the hint through-out the element
    removeAttr(this.nativeElement, 'title');
    this.chartReady = false;
    this.binddataset = this.nativeElement.getAttribute('dataset.bind');
    this.chartEventListener.add(runtimeEventManager.on('wms:replot-chart').subscribe((activeChartScope)=> {
      if (activeChartScope.$id === this.$id && activeChartScope.type === this.type) {
        this._plotChartProxy();
      }
      }
    ))
    this.variableService = runtimeEventManager.getService('variableService');
    // this.locale = runtimeEventManager.getService('i18Services');
    this.debouncedFn = debounce(this.invokeDebounceFn.bind(this), 2000);
  }

    // Generate a random number between min (inclusive) and max (exclusive)
    getRandomInt(min: number, max: number) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min)) + min;
    }

    override ngAfterViewInit() {
      super.ngAfterViewInit();
      if (includes(['outside', 'inside', 'hide'], this.showlabels)) {
        this.showlabelsoutside = this.showlabels === 'inside' ? false : true;
      }

      if (!this.theme) {
        // Default theme for pie/donut is Azure and for other it is Terrestrial
        this.theme = isPieType(this.type) ? 'Azure' : 'Terrestrial';
      }

      this.nativeElement.setAttribute('id', 'wmChart' + this.$id);
      this.widgetDataset = {};

      if (this.widgetId) {
        this.setWidgetTypes();
      }
      // When there is no value binding, then plot the chart with sample data
      if (!this.binddataset && !this.nativeElement.getAttribute('scopedataset')) {
        this._plotChartProxy();
      }

      if (this.binddataset && this.binddataset.indexOf('bind:Variables.')! == 1) {
        let arr = this.dataset.split('.');
        this.variableObj = this.variableService.getVariableByName(arr[1]);
      }

      this.handleDataset();
    }

    updateOnpropChange(propName: string) {
    if (propName === 'dataset' || propName === 'datasource')
      this.setDefaultAxisOptions()
    }

    togglePropertiesByChartType(){
       hideOrShowProperties(this.widgetProps, allOptions, false);
       hideOrShowProperties(this.widgetProps, chartTypes.indexOf(this.type) === -1? options['Column']: options[this.type], true)
    }

    protected override onStyleChange(key: string, nv: any, ov?: any) {
      const styleObj: any = {};
      super.onStyleChange(key, nv, ov);
      switch (key) {
        case 'fontsize':
        case 'fontunit':
        case 'color':
        case 'fontfamily':
        case 'fontweight':
        case 'fontstyle':
        case 'textdecoration':
          styleObj[styleProps[key]] = (key === 'fontsize' || key === 'fontunit') ? this.fontsize + this.fontunit : nv;
          setTextStyle(styleObj, this.$id);
          break;
      }
    }

    protected override onPropertyChange(key: string, nv: any, ov?: any) {
    super.onPropertyChange(key, nv, ov);
    switch (key) {
      case 'shape':
        this._plotChartProxy();
        break;
      case 'dataset':
        this.handleDataset(nv);
        break;
      case 'datasource':
        this.handleDataset();
        break;
      case 'type':
        if (isPieType(this.type)) {
          this.offsettop = 20;
          this.offsetright = 0;
          this.offsetbottom = 0;
          this.offsetleft = 0;
        } else if (ov === 'Pie' || ov === 'Donut') {
          this.offsettop = 25;
          this.offsetright = 25;
          this.offsetbottom = 55;
          this.offsetleft = 75;
        }
        this.togglePropertiesByChartType();
        break;
      case 'groupby':
        if (isArray(nv)) {
          this.groupby = nv.join()
        }
        this.isGroupByChecked = this.isGroupByEnabled(this);
        this.toggleAggregationState();
        // widgetProps.groupby.selectedvalues = newVal;
        if (this.isGroupByChecked) {
          //Filtering x and y axis options based on the data filtering options
          this.filterXAxisOptions();
          this.setOrderByColumns();
        } else {
          //Showing all options
          this.widgetProps['xaxisdatakey'].options = this.getCustomizedOptions('xaxisdatakey');
          this.setYAxisDataKey();
          //If groupby not selected then remove aggregation columns from markup
          runtimeEventManager.emit('update-widget-property',{prop: 'aggregation',value: ''});
          runtimeEventManager.emit('update-widget-property',{prop: 'aggregationcolumn',value: ''});
          // $rootScope.$emit('update-widget-property', 'aggregation', '');
          // $rootScope.$emit('update-widget-property', 'aggregationcolumn', '');
        }
        break;
      case 'aggregationcolumn':
        //Setting the group by columns when aggregation column is changed
        this.setGroupByColumns(nv);
        this.filterYAxisOptions();
        this.setOrderByColumns();
        break;
      case 'aggregation':
        //setting the aggregation columns
        this.toggleAggregationColumnState(nv);
        this.filterYAxisOptions();
        this.setOrderByColumns();
        if (nv !== NONE) {
          //Setting the aggregation columns based on the aggregation function chosen
          this.setAggregationColumns(nv);
        }
        break;
      case 'xaxisdatakey':
        this.xaxisdatakey = nv
        this._plotChartProxy()
        break;
      case 'yaxisdatakey':
        if (isArray(nv)) {
          this.yaxisdatakey = nv.join();
        }
        this._plotChartProxy()
        break;
    }
      if (includes(advanceDataProps, key) || key === 'height') {
        this._plotChartProxy();
      }
  }

  private setDefaultAxisOptions() {
      const defaultColumns = this.getDefaultColumns();
      if (!this.datasource && this.binddataset) {
        return;
      }
      if (this.datasource && this.datasource.category === 'wm.ServiceVariable' && !defaultColumns.length) {
        return;
      }
      //If we get the valid default columns then assign them as the x and y axis
      //In case of service variable we may not get the valid columns because we cannot know the datatypes
      this.xaxisdatakey = defaultColumns[0] || null;
      this.yaxisdatakey = defaultColumns[1] || null;
      runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, 'xaxisdatakey': this.xaxisdatakey, 'yaxisdatakey': this.yaxisdatakey})
      runtimeEventManager.emit('updateShadowProperty')
      // this.$root.$emit('set-markup-attr', scope.widgetid, {'xaxisdatakey': scope.xaxisdatakey, 'yaxisdatakey': scope.yaxisdatakey});
    }
  private getDefaultColumns() {
    let defaultColumns = [],
      type,
      stringColumn,
      columns = this.isLiveVariable ? this.propertiesMap.columns : [],
      i,
      temp;

    for (i = 0; i < columns.length && defaultColumns.length <= 2; i += 1) {
      type = columns[i].type;
      if (!columns[i].isRelated && (isNumberType(type))) {
        defaultColumns.push(columns[i].fieldName);
      } else if (type === 'string' && !stringColumn) {
        stringColumn = columns[i].fieldName;
      }
    }
    //Other than bubble chart x: string type y: number type
    //Bubble chart x: number type y: number type
    if (stringColumn && defaultColumns.length > 0 && !isBubbleChart(this.type)) {
      temp = defaultColumns[0];
      defaultColumns[0] = stringColumn;
      defaultColumns[1] = temp;
    }

    return defaultColumns;
  }
  async _plotChartProxy() {
    this.bundleLoading = true;
    if (!this.scriptLoaderService) {
      return;
    }
    const nvd3Bundle = await this.scriptLoaderService.loadLazyScript('nvd3-bundle');
    const wmNvd3Bundle = await this.scriptLoaderService.loadLazyScript('wm-nvd3-bundle');
    this.bundleLoading = false;
    if(!nvd3Bundle.loaded || !wmNvd3Bundle.loaded) {
      console.error("Error loading nvd3-bundle", nvd3Bundle?.error, wmNvd3Bundle?.error);
      return;
    }
    setTimeout(()=>{this.plotChartProxy()},100);
  }

  plotChartProxy(): any {
    this.showContentLoadError = false;
    this.invalidConfig = false;
    // Checking if x and y axis are chosen
    if (!this.isValidAxis()) {
      this.setErrMsg('MESSAGE_INVALID_AXIS');
      return [];
    }
    this.isLoadInProgress = true;
    const groupingDetails = this.getGroupingDetails();
    // If aggregation/group by/order by properties have been set, then get the aggregated data and plot the result in the chart.
    // TODO: datasource for live variable detection
    if (this.binddataset && this.isLiveVariable && (this.filterFields || this.isDataFilteringEnabled())) {
      this.getAggregatedData(() => this.plotChart());
    } else { // Else, simply plot the chart.
      // In case of live variable resetting the aggregated data to the normal dataset when the aggregation has been removed
      // TODO: add (&& this.dataset.data)  to below condition when data is set
      if (this.dataset && this.isLiveVariable) {
        if (this.isGroupByEnabled(this) && groupingDetails.isVisuallyGrouped) {
          this.chartData = this.getVisuallyGroupedData(this.chartData, groupingDetails.visualGroupingColumn);
        }

      }
      this.plotChart();
    }
  }

    // Check if either groupby, aggregation or orderby is chosen
    isDataFilteringEnabled() {
      /*Query need to be triggered if any of the following cases satisfy
      * 1. Group By and aggregation both chosen
      * 2. Only Order By is chosen
      * */

      return this.isAggregationEnabled(this) || (!this.isVisuallyGrouped && this.orderby);
    }

  plotChart() {
    const element: any = this.nativeElement;
    if (this.isVisuallyGrouped && !isPieType(this.type)) {
      this._processedData = this.chartData;
    } else {
      this._processedData = this.processChartData();
    }

    // if (!element[0].getBoundingClientRect().height) {
    //   return;
    // }
    if (this.clearCanvas) {
      // empty svg to add-new chart
      element.find('svg').replaceWith('<svg></svg>');
      this.clearCanvas = false;
    }

    // In case of invalid axis show no data available message
    if (!this.isValidAxis()) {
      this._processedData = [];
    }
    nv.addGraph(() => this.configureChart(),  () => {
      /*Bubble chart has an time out delay of 300ms in their implementation due to which we
      * won't be getting required data points on attaching events
      * hence delaying it 600ms*/
      setTimeout( () => {
        this.attachClickEvent();
      }, 600);
    });
    this.isLoadInProgress = false;
  }

  attachClickEvent() {

  }

  processChartData() {
    this.sampleData = getSampleData(this);
    // scope variables used to keep the actual key values for x-axis
    this.xDataKeyArr = [];
    if (this.isServiceVariable) {
      this.showContentLoadError = true;
      this.errMsg = this.appLocale.MESSAGE_INFO_SAMPLE_DATA;
      this.hintMsg = this.appLocale.MESSAGE_ERROR_DATA_DISPLAY + this.widget.name;
      return this.sampleData;
    }
    // Plotting the chart with sample data when the chart dataset is not bound
    if (!this.binddataset || isUndefined(this.chartData)) {
      this.xDataKeyArr = getDateList();
      // this.showContentLoadError = true;
      this.errMsg = this.appLocale.MESSAGE_INFO_SAMPLE_DATA;
      return this.sampleData;
    }

    if (!this.chartData || !this.chartData.length) {
      return [];
    }

    let datum = [],
      yAxisKey: any,
      shapes: any = [],
      values: any = [];
    const xAxisKey = this.xaxisdatakey,
      yAxisKeys = this.yaxisdatakey? this.yaxisdatakey.split(',') : [],
      dataSet = this.chartData;

    if (isArray(dataSet)) {
      if (isPieType(this.type)) {
        yAxisKey = yAxisKeys[0];
        datum = map(dataSet, (dataObj, index) => {
          if (!isEmptyObject(dataSet[index])) {
            return this.valueFinder(dataSet[index], xAxisKey, yAxisKey);
          }
        });
        datum = this.getValidData(datum);
      } else {
        if (isBubbleChart(this.type)) {
          shapes = this.shape === 'random' ? allShapes : this.shape;
        }
        yAxisKeys.forEach((yAxisKey: any, series: any) => {
          values =  map(dataSet, (dataObj, index) => {
            if (!isEmptyObject(dataSet[index])) {
              return this.valueFinder(dataSet[index], xAxisKey, yAxisKey, index, (isArray(shapes) && shapes[series]) || this.shape);
            }
          });
          values = this.getValidData(values);
          datum.push({
            values: values,
            key: prettifyLabels(yAxisKey)
          });
        });
      }
    }
    return datum;

  }

    valueFinder(dataObj: any, xKey: any, yKey: any, index?: any, shape?: string) {
      const xVal = this.getxAxisVal(dataObj, xKey, index),
        value = get(dataObj, yKey),
        yVal = parseFloat(value) || value,
        size = parseFloat(dataObj[this.bubblesize]) || 2;
      let dataPoint: any = {};

      if (isChartDataJSON(this.type)) {
        dataPoint.x = xVal;
        dataPoint.y = yVal;
        // only Bubble chart has the third dimension
        if (isBubbleChart(this.type)) {
          dataPoint.size = size;
          dataPoint.shape = shape || 'circle';
        }
      } else if (isChartDataArray(this.type)) {
        dataPoint = [xVal, yVal];
      }
      // Adding actual unwrapped data to chart data to use at the time of selected data point of chart event
      dataPoint._dataObj = dataObj;
      return dataPoint;
    }

    getxAxisVal(dataObj: any, xKey: any, index: number) {
      const value = get(dataObj, xKey);
      // If x axis is other than number type then add indexes
      if (isLineTypeChart(this.type) && !isNumberType(this.xAxisDataType)) {
        // Verification to get the unique data keys
        this.xDataKeyArr.push(value);
        return index;
      }
      return value;
    }
  // prepares and configures the chart properties
  configureChart() {
    // Copy the data only in case of pie chart with default data
    // Reason : when multiple pie charts are bound to same data, first chart theme will be applied to all charts
    let xDomainValues;
    let yDomainValues;
    let chart;
    let beforeRenderVal;
    const yformatOptions: any = {};

    if (this._processedData?.length > 0) {
      if (isAxisDomainValid(this, 'x')) {
        xDomainValues = this.binddataset ? this.getXMinMaxValues(this._processedData[0]) : { 'min' : {'x': 1},  'max' : {'x' : 5}};
      }
      if (isAxisDomainValid(this, 'y')) {
        yDomainValues = this.binddataset ? this.getYMinMaxValues(this._processedData) : { 'min' : {'y' : 1}, 'max' : {'y' : 5}};
      }
    }

    if (isPieType(this.type) && (!this.binddataset || !this.scopedataset)) {
      this._processedData = getClonedObject(this.scopedataset || this._processedData);
    }

    // get the chart object
    chart = initChart(this, xDomainValues, yDomainValues, null, !this.binddataset);

    if (isArray(this._processedData)) {
      let canvasEl = getCanvasElement();
      let chartEl = $(canvasEl).find('#wmChart' + this.$id + ' svg');

      // WMS-19499:  To remove chart X-axis old ticks when chart data loaded dynamically.
      const oldgTicks =  $(chartEl).find('g.nv-x').find('g.tick');
      if (oldgTicks && oldgTicks.length) {
        oldgTicks.remove();
      }
      // beforeRenderVal = this.invokeEventCallback('beforerender', { 'chartInstance' : chart});
      // if (beforeRenderVal) {
      //   chart = beforeRenderVal;
      // }
      this.chart = chart;
      // changing the default no data message
      d3.select(chartEl[0])
        .datum(this._processedData)
        .call(this.chart);
      this.postPlotProcess(chart);
      return chart;
    }
  }

  getXMinMaxValues(datum: any) {
    if (!datum) {
      return;
    }
    const xValues: any = {};
    /*
     compute the min x value
     eg: When data has objects
        input: [{x:1, y:2}, {x:2, y:3}, {x:3, y:4}]
        min x: 1
     eg: When data has arrays
        input: [[10, 20], [20, 30], [30, 40]];
        min x: 10
    */
    xValues.min = minBy(datum.values, dataObject => dataObject.x || dataObject[0]);
    /*
     compute the max x value
     eg: When data has objects
        input: [{x:1, y:2}, {x:2, y:3}, {x:3, y:4}]
        max x: 3
     eg: When data has arrays
        input: [[10, 20], [20, 30], [30, 40]];
        max x: 30
     */
    xValues.max = maxBy(datum.values, dataObject => dataObject.x || dataObject[0]);
    return xValues;
  }

  // Getting the min and max values among all the y values
  getYMinMaxValues(datum: any) {
    const yValues: any = {},
      minValues: any[] = [],
      maxValues: any[] = [];
    if (!datum) {
      return;
    }

    /*
     Getting the min and max y values among all the series of data
     compute the min y value
     eg: When data has objects
        input: [[{x:1, y:2}, {x:2, y:3}, {x:3, y:4}], [{x:2, y:3}, {x:3, y:4}, {x:4, y:5}]]
        min y values : '2'(among first set) & '3'(among second set)
        max y values : '4'(among first set) & '5'(among second set)

     eg: When data has arrays
        input: [[[10, 20], [20, 30], [30, 40]], [[20, 30], [30, 40], [40, 50]]]
        min y values : '20'(among first set) & '30'(among second set)
        max y values : '40'(among first set) & '50'(among second set)
     */

    forEach(datum, (data: any) => {
      minValues.push(minBy(data.values, function (dataObject) { return dataObject.y || dataObject[1]; }));
      maxValues.push(maxBy(data.values, function (dataObject) { return dataObject.y || dataObject[1]; }));
    });
    // Gets the least and highest values among all the min and max values of respective series of data
    yValues.min = minBy(minValues, dataObject => dataObject.y || dataObject[1]);
    yValues.max = maxBy(maxValues, dataObject => dataObject.y || dataObject[1]);
    return yValues;
  }

  postPlotProcess(chart: any) {
    postPlotChartProcess(this);

  }

  getCustomizedOptions(prop: string, fields?: any) {
    let activeWidget = this;

    const groupByColumns = split(activeWidget.groupby, ','),
      aggColumns = split(activeWidget.aggregationcolumn, ',');
    if (!activeWidget.binddataset) {
      return fields;
    }
    if (!activeWidget.axisoptions) {
      this.axisoptions = fields;
    }
    let newOptions;
    switch (prop) {
      case 'xaxisdatakey':
        // If group by enabled, columns chosen in groupby will be populated in x axis options
        if (activeWidget.isGroupByEnabled(activeWidget)) {
          newOptions = groupByColumns;
        }
        break;
      case 'yaxisdatakey':
        // If aggregation by enabled, columns chosen in aggregation will be populated in y axis options
        if (activeWidget.isAggregationEnabled(activeWidget)) {
          newOptions = aggColumns;
        } else if (activeWidget.isLiveVariable) {
          // In case of live variable populating only numeric columns
          newOptions = activeWidget.numericColumns;
        }
        break;
      case 'groupby':
        // Filtering only non primary key columns
        if (activeWidget.isLiveVariable && activeWidget.nonPrimaryColumns && activeWidget.nonPrimaryColumns.length) {
          newOptions = activeWidget.nonPrimaryColumns;
        }
        break;
      case 'aggregationcolumn':
        // Set the 'aggregationColumn' to show all keys in case of aggregation function is count or to numeric keys in all other cases.
        if (activeWidget.isLiveVariable && activeWidget.isAggregationEnabled(activeWidget) && activeWidget.aggregation !== 'count') {
          newOptions = activeWidget.numericColumns;
        }
        break;
      case 'orderby':
        // Set the 'aggregationColumn' to show all keys in case of aggregation function is count or to numeric keys in all other cases.
        if (activeWidget.isLiveVariable && activeWidget.isAggregationEnabled(activeWidget)) {
          newOptions = uniq(concat(groupByColumns, aggColumns));
        }
        break;
      case 'bubblesize':
        if (this.numericColumns && this.numericColumns.length) {
          newOptions = activeWidget.numericColumns;
        }
        break;
    }

    return newOptions || fields || activeWidget.axisoptions;
  }


    private handleDataset(dataset?: any) {
    if (this.binddataset){
      let arr = this.binddataset.split('.');
      this.variableObj = this.datasource;
      this.isServiceVariable = this.variableObj && (this.variableObj.category === 'wm.ServiceVariable' || this.variableObj.category === 'wm.WebSocketVariable');
    }
      let newVal = dataset || this.dataset || this.variableObj?.dataSet;
      this.errMsg = '';
      // Resetting the flag to false when the binding was removed
      if (!newVal && !this.binddataset) {
        this.isVisuallyGrouped = false;
        this.axisoptions = null;
        return;
      }

      this.isLiveVariable = this.variableObj && this.variableObj.category === 'wm.LiveVariable'
      if (this.isLiveVariable) {
        // TODO: remove this line when the dataset is set
        set(this, 'propertiesMap', this.variableObj.propertiesMap);
      }

      //If binded to a live variable feed options to the aggregation and group by
      if (this.isLiveVariable) {
        //Updating the numeric and non primary columns when dataset is changed
        this.setNumericandNonPrimaryColumns(this.propertiesMap);
      }

      this.chartData = this.isLiveVariable ? (get(newVal , 'data') || newVal) : (newVal && newVal.dataValue === '' && keys(newVal).length === 1) ? '' : newVal;
      // if the data returned is an object make it an array of object
      if (!isArray(this.chartData) && isObject(this.chartData)) {
        this.chartData = [this.chartData];
      }

      runtimeEventManager.emit('updatePropPanelOptions',this);
      this.hideOrShowProperties(advanceDataProps, this.isLiveVariable);

      if (newVal && newVal.filterFields) {
        this.filterFields = newVal.filterFields;
      }

      // plotchart for only valid data and only after bound variable returns data
      if (this.chartData) {
        this._plotChartProxy();
      }
    }

    private getValidData(values: any[]) {
      return (values.length === 1 && values[0] === undefined) ? [] : values;return [];
    }

    setNumericandNonPrimaryColumns(propertiesMap: any) {
      let columns: any,
        type;
      this.numericColumns = [];
      this.nonPrimaryColumns = [];
      //Fetching all the columns
      if (this.binddataset) {
        columns = fetchPropertiesMapColumns(this.propertiesMap);
      }

      if (columns) {
        //Iterating through all the columns and fetching the numeric and non primary key columns
        forEach(Object.keys(columns), (key) => {
          type = columns[key].type;
          if (isNumberType(type)) {
            this.numericColumns.push(key);
          }
          //Hiding only table's primary key
          if (columns[key].isRelatedPk === 'true' || !columns[key].isPrimaryKey) {
            this.nonPrimaryColumns.push(key);
          }
        });
        this.numericColumns = this.numericColumns.sort();
        this.nonPrimaryColumns = this.nonPrimaryColumns.sort();
      }
    }

    hideOrShowProperties(properties: any, show: boolean) {
      forEach(properties, (property: any) => {
        if (!isUndefined(this.widgetProps[property].show) && !isUndefined(show)) {
          this.widgetProps[property].show = show
        }
      })
    }

    getGroupingDetails() {
      if (this.isGroupByEnabled(this) && !this.isAggregationEnabled(this)) {
        let isVisuallyGrouped = false,
          visualGroupingColumn,
          groupingExpression,
          columns: any = [],
          groupingColumnIndex;
        const groupbyColumns = this.groupby && this.groupby !== NONE? this.groupby.split(','): [],
          yAxisKeys = this.yaxisdatakey ? this.yaxisdatakey.split(','): [];

        if (groupbyColumns.length > 1) {
          /*Getting the group by column which is not selected either in x or y axis*/
          groupbyColumns.every((column: any, index: number) => {
            if (this.xaxisdatakey !== column && $.inArray(column, yAxisKeys) === -1) {
              isVisuallyGrouped = true;
              visualGroupingColumn = column;
              groupingColumnIndex = index;
              groupbyColumns.splice(groupingColumnIndex, 1);
              return false;
            }
            return true;
          })
          // Constructing the groupby expression
          if (visualGroupingColumn) {
            columns.push(visualGroupingColumn);
          }

          if (groupbyColumns.length) {
            columns = concat(columns, groupbyColumns);
          }
        }
        // If x and y axis are not included in aggregation need to be included in groupby
        if (this.xaxisdatakey !== this.aggregationcolumn) {
          columns.push(this.xaxisdatakey);
        }
        forEach(yAxisKeys, key => {
          if (key !== this.aggregationcolumn) {
            columns.push(key);
          }
        });
        groupingExpression =  columns.join(',');
        // set isVisuallyGrouped flag in scope for later use
        this.isVisuallyGrouped = isVisuallyGrouped;

        return {
          expression: groupingExpression,
          isVisuallyGrouped: isVisuallyGrouped,
          visualGroupingColumn: visualGroupingColumn
        };
      }
      return {
        expression: '',
        isVisuallyGrouped: false,
        visualGroupingColumn: ''
      };
    }

    private getAggregatedData(callback: () => void) {
      const variable: any = this.datasource,
        yAxisKeys = this.yaxisdatakey ? this.yaxisdatakey.split(',') : [],
        data: any = {};
      let sortExpr: any,
        columns: any = [],
        colAlias: any,
        orderByColumns,
        groupByFields: any = [];

      if (!variable) {
        return;
      }
      if (this.isGroupByEnabled(this)) {
        groupByFields = split(this.groupby, ',');
      }
      if (this.orderby) {
        sortExpr = replace(this.orderby, /:/g, ' ');
        columns = uniq(concat(columns, groupByFields, [this.aggregationcolumn]));
        orderByColumns = getLodashOrderByFormat(this.orderby).columns;
        // If the orderby column is chosen either in groupby or orderby then replace . with $ for that column
        forEach(intersection(columns, orderByColumns), col => {
          colAlias = getValidAliasName(col);
          sortExpr = replace(sortExpr, col, colAlias);
        });
      }
      if (this.isAggregationEnabled(this)) {
        // Send the group by in the aggregations api only if aggregation is also chosen
        data.groupByFields = groupByFields;
        data.aggregations =  [
          {
            'field': this.aggregationcolumn,
            'type':  aggregationFnMap[this.aggregation],
            'alias': getValidAliasName(this.aggregationcolumn)
          }
        ];
      }
      if (!(variable && isFunction(variable.execute))) {
        return;
      }
      // Execute the query.
      this.debouncedFn({variable, data, sortExpr, yAxisKeys, callback});
    }

    invokeDebounceFn(args: any) {
      args.variable.execute('getAggregatedData', {
        'aggregations' : args.data,
        'sort'         : args.sortExpr
      }).then((response: any) => {
        // Transform the result into a format supported by the chart.
        const chartData: any = [],
          aggregationAlias: any = getValidAliasName(this.aggregationcolumn),
          xAxisAliasKey: any = getValidAliasName(this.xaxisdatakey),
          yAxisAliasKeys: any = [];

        args.yAxisKeys.forEach((yAxisKey: any) => yAxisAliasKeys.push(getValidAliasName(yAxisKey)));

        forEach(response.body.content, (responseContent) => {
          const obj: any = {};
          // Set the response in the chartData based on 'aggregationColumn', 'xAxisDataKey' & 'yAxisDataKey'.
          if (this.isAggregationEnabled(this)) {
            obj[this.aggregationcolumn] = responseContent[aggregationAlias];
            obj[this.aggregationcolumn] = get(responseContent, aggregationAlias) || get(responseContent, this.aggregationcolumn);
          }

          obj[this.xaxisdatakey] = get(responseContent, xAxisAliasKey) || get(responseContent, this.xaxisdatakey);

          args.yAxisKeys.forEach((yAxisKey: any, index: number) => {
            obj[yAxisKey] = responseContent[yAxisAliasKeys[index]];
            obj[yAxisKey] = get(responseContent, yAxisAliasKeys[index]) || get(responseContent, yAxisKey);
          });

          chartData.push(obj);
        });

        this.chartData = chartData;

        triggerFn(args.callback);
      }, () => {
        this.chartData = [];
        this.setErrMsg('MESSAGE_ERROR_FETCH_DATA');
        triggerFn(args.callback);
      });
    }



    setErrMsg(message: string) {
      if (this.showNoDataMsg) {
        this.showContentLoadError = true;
        this.invalidConfig = true;
        // TODO: Set the locale from the message
        this.errMsg = this.appLocale[message];
        // this.errMsg = this.locale.appLocale[message]; // $rootScope.locale[message];
      }
    }

    private getVisuallyGroupedData(queryResponse: any, groupingColumn: any) {
      let groupData: any = {},
        groupValues: any = [],
        orderByDetails,
        maxLength: number;
      const chartData: any = [],
        _isAreaChart = isAreaChart(this.type),
        yAxisKey = first(split(this.yaxisdatakey, ','));
      this.xDataKeyArr = [];
      queryResponse = orderBy(queryResponse, split(this.groupby, ','));
      if (this.orderby) {
        orderByDetails = getLodashOrderByFormat(this.orderby);
        queryResponse = orderBy(queryResponse, orderByDetails.columns, orderByDetails.orders);
      }
      queryResponse = groupBy(queryResponse, groupingColumn);
      // In case of area chart all the series data should be of same length
      if (_isAreaChart) {
        maxLength = max(map(queryResponse, obj => obj.length));
      }
      forEach(queryResponse, (values, groupKey) => {
        groupValues = isAreaChart(this.type) ? fill(new Array(maxLength), [0, 0]) : [];
        forEachRight(values, (value, index) => {
          groupValues[index] = this.valueFinder(value, this.xaxisdatakey, yAxisKey, index);
        });
        groupData = {
          key : groupKey,
          values : groupValues
        };
        chartData.push(groupData);
      });
      return chartData;
    }

    private toggleAggregationState() {
      this.widgetProps['aggregation'].disabled = !this.isGroupByChecked;
      if (!this.isGroupByChecked) {
        this.isVisuallyGrouped = false;
      }
      this.toggleAggregationColumnState();
    }

    private toggleAggregationColumnState(aggregation?: any) {
      this.widgetProps['aggregationcolumn'].disabled = !this.isGroupByChecked || (aggregation && aggregation === NONE);
    }
    private filterXAxisOptions() {
      let xAxisOptions = [];
      //TODO: also add the condition isobject(this.dataset)
      if (this.binddataset) {
        if (this.isGroupByEnabled(this)) {
          xAxisOptions = this.groupby.split(',');
          this.xaxisdatakey = xAxisOptions[0];
          this.widgetProps['xaxisdatakey'].options = xAxisOptions;
        }
      }
      runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, 'xaxisdatakey': this.xaxisdatakey})
      // scope.$root.$emit('set-markup-attr', scope.widgetid, {'xaxisdatakey': scope.xaxisdatakey});
    }

    private filterYAxisOptions() {
      let yAxisOptions = [],
        isAggregationApplied = (this.isGroupByEnabled(this) && this.aggregation && this.aggregation !== 'none' && this.aggregationcolumn);
      //Check if the data-set has been bound and the value is available in data-set.
      //TODO: add  && isObject(this.dataset) to below condition
      if (this.binddataset) {
        //If 'aggregation' is not 'none' and if the 'aggregationColumn' has not already been added into the axesOptions, then add it.
        if (isAggregationApplied) {
          yAxisOptions.push(this.aggregationcolumn);
          //Choosing first column in aggregation by as y axis
          this.yaxisdatakey = yAxisOptions[0];
          //Setting y axis options with aggregation columns
          this.setYAxisDataKey(yAxisOptions);
        } else {
          //Setting y axis options with aggregation columns
          this.setYAxisDataKey();
        }
      }
      runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, 'yaxisdatakey': this.yaxisdatakey})
    }
    private setOrderByColumns() {
      this.widgetProps['orderby'].options = this.getCustomizedOptions('orderby');
      if (this.widgetProps['orderby'].options) {
        this.updateOrderByExpr();
      }
    }

    private setYAxisDataKey(options?: any) {
      options = options || this.getCustomizedOptions('yaxisdatakey');
      if (isPieType(this.type)) {
        this.widgetProps['yaxisdatakey'].widget = 'list';
        this.widgetProps['yaxisdatakey'].options = options;
      } else {
        set(this.widgetDataset,'yaxisdatakey',options || []);
      }
    }

    //Validates and returns valid sort epxression
    private updateOrderByExpr() {
      let orderByConfig = getLodashOrderByFormat(this.orderby),
        columns = orderByConfig.columns,
        orders = orderByConfig.orders,
        orderByOptions = this.widgetProps['orderby'].options,
        formats: any = [];
      //Adding only valid columns which are present in options
      forEach(columns, (col, index)=> {
        if (includes(orderByOptions, col)) {
          formats.push(join([col, orders[index]], ':'));
        }
      });
      this.orderby = join(formats, ',');
    }

    private setGroupByColumns(aggregationcolumn: any) {
      let index,
        columns: any = getClonedObject(this.nonPrimaryColumns);
      //Removing the aggregation column out of the non primary columns
      if (this.nonPrimaryColumns && aggregationcolumn) {
        index = indexOf(this.nonPrimaryColumns, aggregationcolumn);
        if (index >= 0) {
          columns.splice(index, 1);
        }
      }
    }

    private setAggregationColumns(aggregation: any) {
      this.widgetProps['aggregationcolumn'].options = aggregation === 'count' ? this.axisoptions : this.numericColumns;
    }

    setWidgetTypes() {
      if (isPieType(this.type)) {
        // If pie chart, set the display key for x and y axis datakey and subgroups
        this.widgetProps['xaxisdatakey'].displayKey = 'LABEL_PROPERTY_LABEL';
        this.widgetProps['yaxisdatakey'].displayKey = 'LABEL_PROPERTY_VALUES';
        PropertiesFactory.getPropertyGroup('xaxis').displayKey = 'LABEL_PROPERTY_LABEL_DATA';
        PropertiesFactory.getPropertyGroup('yaxis').displayKey = 'LABEL_PROPERTY_VALUE_DATA';

        // If it is a pie chart then the yaxisdatakey must be a single select else it has to be a multiselect
        this.widgetProps['yaxisdatakey'].widget = 'list';
        this.widgetProps['groupby'].widget = 'list';
      } else {
        this.widgetProps['xaxisdatakey'].displayKey = undefined;
        this.widgetProps['yaxisdatakey'].displayKey = undefined;
        PropertiesFactory.getPropertyGroup('xaxis').displayKey = undefined;
        PropertiesFactory.getPropertyGroup('yaxis').displayKey = undefined;

        this.widgetProps['yaxisdatakey'].widget = 'multi-select';
        this.widgetProps['groupby'].widget = 'multi-select';
      }
    }
  }

